/**
 * ProseMirror comment-highlight decorations (BLA-70 / B1).
 *
 * A comment is **additive annotation** (ADR-031): it must never become part of
 * the stored document. So it is rendered as a ProseMirror *decoration* — a
 * view-layer overlay that never serializes into the markdown — and never as a
 * mark, which would bake it into the document bytes. The durable anchor lives in
 * the `document_comments` row; this recomputes the highlight from the live doc
 * text on every render (via the covered {@link findQuotedRange}), so a highlight
 * tracks in-session edits and re-anchors on reload, and simply disappears when
 * the quoted text can no longer be found (the sidebar shows the fail-loud "text
 * changed" note instead of a wrong highlight).
 *
 * This is DOM/ProseMirror glue that jsdom cannot drive; it carries the ADR-030
 * coverage exclude and is guarded by `e2e/document-comments.spec.ts`. The pure
 * text-search logic it depends on lives in `commentAnchor.ts` and IS covered.
 */
import { Extension } from "@tiptap/core";
import type { Editor } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import type { DocumentComment } from "../../types";
import { findQuotedRange } from "./commentAnchor";

/** The plugin key for the comment-decoration state. */
export const commentPluginKey = new PluginKey<CommentPluginState>(
  "commentDecorations",
);

/** The comment list + active/hovered id the plugin renders. */
interface CommentPluginState {
  comments: DocumentComment[];
  activeId: string | null;
  hoveredId: string | null;
}

/** Build a plain-text projection of the doc + a char-index → PM-position map. */
export function docTextWithMap(doc: PMNode): { text: string; map: number[] } {
  let text = "";
  const map: number[] = [];
  let separated = true;
  doc.descendants((node: PMNode, pos: number) => {
    if (node.isText && node.text) {
      for (let i = 0; i < node.text.length; i += 1) {
        text += node.text[i];
        map.push(pos + i);
      }
      separated = false;
    } else if (!separated && node.isBlock) {
      text += " ";
      map.push(pos);
      separated = true;
    }
    return true;
  });
  return { text, map };
}

/** Resolve a comment's quoted text to a ProseMirror `{ from, to }` range. */
function findDocRange(
  text: string,
  map: number[],
  quoted: string,
  prefix?: string | null,
): { from: number; to: number } | null {
  const found = findQuotedRange(text, quoted, prefix);
  if (!found || found.to - 1 >= map.length) return null;
  return { from: map[found.from], to: map[found.to - 1] + 1 };
}

/** Compute the decoration set for a comment list against a doc. */
function buildDecorations(
  doc: PMNode,
  comments: DocumentComment[],
  activeId: string | null,
  hoveredId: string | null,
): DecorationSet {
  const decorations: Decoration[] = [];
  if (comments.length === 0) return DecorationSet.create(doc, decorations);

  const { text, map } = docTextWithMap(doc);
  for (const comment of comments) {
    const quoted = comment.anchor?.quoted_text ?? "";
    if (quoted === "") continue;
    const range = findDocRange(text, map, quoted, comment.anchor?.prefix);
    if (!range) continue;
    const classes = ["comment-highlight"];
    if (comment.id === activeId) classes.push("comment-highlight-active");
    if (comment.id === hoveredId) classes.push("comment-highlight-hover");
    decorations.push(
      Decoration.inline(range.from, range.to, {
        class: classes.join(" "),
        "data-comment-id": comment.id,
      }),
    );
  }
  return DecorationSet.create(doc, decorations);
}

/**
 * A TipTap extension that renders comment anchors as inline decorations.
 *
 * The comment list is pushed in via {@link setCommentDecorations} (a metadata
 * transaction), so it can update as the async comment fetch resolves without
 * re-creating the editor. Clicking a highlight is handled by the React layer in
 * `TipTapEditor` (reading the decoration's `data-comment-id`), not here — so the
 * extension needs no React options and can be built once.
 */
export const CommentDecoration = Extension.create({
  name: "commentDecoration",

  addProseMirrorPlugins() {
    return [
      new Plugin<CommentPluginState>({
        key: commentPluginKey,
        state: {
          init: () => ({ comments: [], activeId: null, hoveredId: null }),
          apply(tr, value) {
            return tr.getMeta(commentPluginKey) ?? value;
          },
        },
        props: {
          decorations(state) {
            const pluginState = commentPluginKey.getState(state);
            if (!pluginState) return null;
            return buildDecorations(
              state.doc,
              pluginState.comments,
              pluginState.activeId,
              pluginState.hoveredId,
            );
          },
        },
      }),
    ];
  },
});

/**
 * Push a comment list + active/hovered id into the editor's decoration plugin.
 *
 * @param editor - The TipTap editor instance.
 * @param comments - The comments to render as highlights.
 * @param activeId - The comment id to render as active (click), or null.
 * @param hoveredId - The comment id to render as hovered (BLA-318), or null.
 */
export function setCommentDecorations(
  editor: Editor,
  comments: DocumentComment[],
  activeId: string | null,
  hoveredId: string | null = null,
): void {
  const { state, view } = editor;
  view.dispatch(
    state.tr.setMeta(commentPluginKey, { comments, activeId, hoveredId }),
  );
}
